import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { format } from 'date-fns';
import { X, Download, FileText } from 'lucide-react';

interface DEAReportModalProps {
  onClose: () => void;
}

export function DEAReportModal({ onClose }: DEAReportModalProps) {
  const [reportType, setReportType] = useState<string>('form222');
  const [dateRange, setDateRange] = useState({
    start: format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });
  const [selectedSchedule, setSelectedSchedule] = useState<string>('all');
  const [selectedDrug, setSelectedDrug] = useState<string>('all');

  // Fetch data based on report type
  const { data: reportData, isLoading } = useQuery({
    queryKey: ['dea-report', reportType, dateRange, selectedSchedule, selectedDrug],
    queryFn: async () => {
      switch (reportType) {
        case 'form222': {
          // Schedule II receipts requiring Form 222
          const { data, error } = await supabase
            .from('dea_form222_receipt_log')
            .select('*')
            .gte('transaction_date', dateRange.start)
            .lte('transaction_date', dateRange.end)
            .order('transaction_date', { ascending: false });

          if (error) throw error;
          return data;
        }

        case 'daily_usage': {
          // Daily usage summary
          let query = supabase
            .from('dea_daily_usage_summary')
            .select('*')
            .gte('transaction_date', dateRange.start)
            .lte('transaction_date', dateRange.end)
            .order('transaction_date', { ascending: false });

          if (selectedSchedule !== 'all') {
            query = query.eq('dea_schedule', selectedSchedule);
          }

          if (selectedDrug !== 'all') {
            query = query.eq('drug_name', selectedDrug);
          }

          const { data, error } = await query;
          if (error) throw error;
          return data;
        }

        case 'inventory': {
          // Current inventory
          let query = supabase.from('dea_current_inventory').select('*');

          if (selectedSchedule !== 'all') {
            query = query.eq('dea_schedule', selectedSchedule);
          }

          if (selectedDrug !== 'all') {
            query = query.eq('drug_name', selectedDrug);
          }

          const { data, error } = await query.order('drug_name');
          if (error) throw error;
          return data;
        }

        case 'discrepancies': {
          // Discrepancy report
          const { data, error } = await supabase
            .from('dea_discrepancy_report')
            .select('*')
            .gte('transaction_date', dateRange.start)
            .lte('transaction_date', dateRange.end)
            .order('discrepancy_resolved', { ascending: true });

          if (error) throw error;
          return data;
        }

        case 'unverified_waste': {
          // Unverified waste transactions
          const { data, error } = await supabase.from('dea_unverified_waste').select('*');

          if (error) throw error;
          return data;
        }

        case 'provider_activity': {
          // Provider activity log
          const { data, error } = await supabase
            .from('dea_provider_activity')
            .select('*')
            .gte('transaction_date', dateRange.start)
            .lte('transaction_date', dateRange.end)
            .order('provider_name', { ascending: true });

          if (error) throw error;
          return data;
        }

        default:
          return [];
      }
    },
  });

  // Fetch unique drug names for filter
  const { data: drugList } = useQuery({
    queryKey: ['dea-drug-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dea_controlled_substance_log')
        .select('drug_name')
        .is('deleted_at', null)
        .order('drug_name');

      if (error) throw error;

      // Get unique drug names
      const uniqueDrugs = Array.from(new Set(data.map((d: any) => d.drug_name)));
      return uniqueDrugs;
    },
  });

  const exportToCSV = () => {
    if (!reportData || reportData.length === 0) return;

    // Convert data to CSV
    const headers = Object.keys(reportData[0]).join(',');
    const rows = reportData.map((row: any) =>
      Object.values(row)
        .map((val) => {
          // Handle values with commas or quotes
          const stringVal = String(val ?? '');
          return stringVal.includes(',') || stringVal.includes('"')
            ? `"${stringVal.replace(/"/g, '""')}"`
            : stringVal;
        })
        .join(',')
    );

    const csv = [headers, ...rows].join('\n');

    // Create download link
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dea_${reportType}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    window.print();
  };

  const getReportTitle = () => {
    switch (reportType) {
      case 'form222':
        return 'DEA Form 222 Receipt Log (Schedule II)';
      case 'daily_usage':
        return 'Daily Controlled Substance Usage Summary';
      case 'inventory':
        return 'Current Controlled Substance Inventory';
      case 'discrepancies':
        return 'Discrepancy Report';
      case 'unverified_waste':
        return 'Unverified Waste Transactions';
      case 'provider_activity':
        return 'Provider Activity Log';
      default:
        return 'DEA Report';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-8">
      <div className="bg-navy-900 border border-cyan-500/30 rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-cyan-500/20">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-gold-400" />
            <div>
              <h2 className="text-2xl font-bold font-rajdhani">Generate DEA Report</h2>
              <p className="text-sm text-gray-400">DEA 21 CFR 1304 Compliant</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Report Configuration */}
        <div className="p-6 border-b border-cyan-500/20">
          <div className="grid grid-cols-4 gap-4">
            {/* Report Type */}
            <div className="col-span-2">
              <label className="block text-sm text-gray-400 mb-2">Report Type</label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full bg-navy-800 border border-cyan-500/30 rounded px-3 py-2 text-white"
              >
                <option value="form222">DEA Form 222 Receipt Log</option>
                <option value="daily_usage">Daily Usage Summary</option>
                <option value="inventory">Current Inventory</option>
                <option value="discrepancies">Discrepancy Report</option>
                <option value="unverified_waste">Unverified Waste</option>
                <option value="provider_activity">Provider Activity Log</option>
              </select>
            </div>

            {/* Date Range */}
            {reportType !== 'inventory' && reportType !== 'unverified_waste' && (
              <>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">From</label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    className="w-full bg-navy-800 border border-cyan-500/30 rounded px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">To</label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    className="w-full bg-navy-800 border border-cyan-500/30 rounded px-3 py-2 text-white"
                  />
                </div>
              </>
            )}
          </div>

          {/* Additional Filters */}
          {(reportType === 'daily_usage' || reportType === 'inventory') && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Filter by Schedule</label>
                <select
                  value={selectedSchedule}
                  onChange={(e) => setSelectedSchedule(e.target.value)}
                  className="w-full bg-navy-800 border border-cyan-500/30 rounded px-3 py-2 text-white"
                >
                  <option value="all">All Schedules</option>
                  <option value="II">Schedule II</option>
                  <option value="III">Schedule III</option>
                  <option value="IV">Schedule IV</option>
                  <option value="V">Schedule V</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Filter by Drug</label>
                <select
                  value={selectedDrug}
                  onChange={(e) => setSelectedDrug(e.target.value)}
                  className="w-full bg-navy-800 border border-cyan-500/30 rounded px-3 py-2 text-white"
                >
                  <option value="all">All Drugs</option>
                  {drugList?.map((drug) => (
                    <option key={drug} value={drug}>
                      {drug}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Export Buttons */}
          <div className="flex gap-3 mt-4">
            <button
              onClick={exportToCSV}
              disabled={!reportData || reportData.length === 0}
              className="flex items-center gap-2 bg-gold-500/20 text-gold-400 px-4 py-2 rounded border border-gold-500/30 hover:bg-gold-500/30 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={exportToPDF}
              disabled={!reportData || reportData.length === 0}
              className="flex items-center gap-2 bg-cyan-500/20 text-cyan-400 px-4 py-2 rounded border border-cyan-500/30 hover:bg-cyan-500/30 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              Export PDF (Print)
            </button>
          </div>
        </div>

        {/* Report Preview */}
        <div className="flex-1 overflow-auto p-6">
          <div className="bg-white text-black rounded-lg p-8" id="report-content">
            {/* Report Header */}
            <div className="mb-6 pb-4 border-b-2 border-gray-800">
              <h1 className="text-2xl font-bold mb-2">{getReportTitle()}</h1>
              <div className="text-sm text-gray-600">
                <div>Generated: {format(new Date(), 'MMMM dd, yyyy h:mm a')}</div>
                {reportType !== 'inventory' && reportType !== 'unverified_waste' && (
                  <div>
                    Date Range: {format(new Date(dateRange.start), 'MMM dd, yyyy')} -{' '}
                    {format(new Date(dateRange.end), 'MMM dd, yyyy')}
                  </div>
                )}
                {selectedSchedule !== 'all' && <div>Schedule: {selectedSchedule}</div>}
                {selectedDrug !== 'all' && <div>Drug: {selectedDrug}</div>}
              </div>
            </div>

            {/* Report Content */}
            {isLoading ? (
              <div className="text-center py-8 text-gray-500">Generating report...</div>
            ) : !reportData || reportData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No data available for the selected criteria
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-800">
                      {reportType === 'form222' && (
                        <>
                          <th className="text-left py-2 px-2">Date</th>
                          <th className="text-left py-2 px-2">Drug Name</th>
                          <th className="text-left py-2 px-2">Strength</th>
                          <th className="text-right py-2 px-2">Quantity</th>
                          <th className="text-left py-2 px-2">Form 222 #</th>
                          <th className="text-left py-2 px-2">Supplier</th>
                          <th className="text-left py-2 px-2">Received By</th>
                        </>
                      )}
                      {reportType === 'daily_usage' && (
                        <>
                          <th className="text-left py-2 px-2">Date</th>
                          <th className="text-left py-2 px-2">Drug</th>
                          <th className="text-center py-2 px-2">Schedule</th>
                          <th className="text-right py-2 px-2">Administered</th>
                          <th className="text-right py-2 px-2">Wasted</th>
                          <th className="text-center py-2 px-2"># Admin</th>
                          <th className="text-center py-2 px-2"># Waste</th>
                          <th className="text-center py-2 px-2"># Discrepancies</th>
                        </>
                      )}
                      {reportType === 'inventory' && (
                        <>
                          <th className="text-left py-2 px-2">Drug Name</th>
                          <th className="text-left py-2 px-2">Strength</th>
                          <th className="text-center py-2 px-2">Schedule</th>
                          <th className="text-left py-2 px-2">Lot #</th>
                          <th className="text-left py-2 px-2">Expires</th>
                          <th className="text-right py-2 px-2">Quantity</th>
                          <th className="text-left py-2 px-2">Last Transaction</th>
                        </>
                      )}
                      {reportType === 'discrepancies' && (
                        <>
                          <th className="text-left py-2 px-2">Date</th>
                          <th className="text-left py-2 px-2">Drug</th>
                          <th className="text-center py-2 px-2">Schedule</th>
                          <th className="text-left py-2 px-2">Type</th>
                          <th className="text-right py-2 px-2">Amount</th>
                          <th className="text-left py-2 px-2">Status</th>
                          <th className="text-left py-2 px-2">Notes</th>
                        </>
                      )}
                      {reportType === 'unverified_waste' && (
                        <>
                          <th className="text-left py-2 px-2">Date</th>
                          <th className="text-left py-2 px-2">Drug</th>
                          <th className="text-center py-2 px-2">Schedule</th>
                          <th className="text-right py-2 px-2">Amount Wasted</th>
                          <th className="text-left py-2 px-2">Provider</th>
                          <th className="text-left py-2 px-2">Patient</th>
                          <th className="text-right py-2 px-2">Hours Pending</th>
                        </>
                      )}
                      {reportType === 'provider_activity' && (
                        <>
                          <th className="text-left py-2 px-2">Provider</th>
                          <th className="text-left py-2 px-2">DEA #</th>
                          <th className="text-left py-2 px-2">Date</th>
                          <th className="text-left py-2 px-2">Type</th>
                          <th className="text-left py-2 px-2">Drug</th>
                          <th className="text-right py-2 px-2">Quantity</th>
                          <th className="text-left py-2 px-2">Patient</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((row: any, index: number) => (
                      <tr key={index} className="border-b border-gray-300">
                        {reportType === 'form222' && (
                          <>
                            <td className="py-2 px-2">
                              {format(new Date(row.transaction_date), 'MM/dd/yyyy')}
                            </td>
                            <td className="py-2 px-2">{row.drug_name}</td>
                            <td className="py-2 px-2">{row.drug_strength}</td>
                            <td className="py-2 px-2 text-right">
                              {row.quantity_received} {row.unit_of_measure}
                            </td>
                            <td className="py-2 px-2">{row.form_222_number || '—'}</td>
                            <td className="py-2 px-2">{row.supplier_name || '—'}</td>
                            <td className="py-2 px-2">{row.received_by_name}</td>
                          </>
                        )}
                        {reportType === 'daily_usage' && (
                          <>
                            <td className="py-2 px-2">
                              {format(new Date(row.transaction_date), 'MM/dd/yyyy')}
                            </td>
                            <td className="py-2 px-2">{row.drug_name}</td>
                            <td className="py-2 px-2 text-center">{row.dea_schedule}</td>
                            <td className="py-2 px-2 text-right">{row.total_administered}</td>
                            <td className="py-2 px-2 text-right">{row.total_wasted}</td>
                            <td className="py-2 px-2 text-center">{row.administration_count}</td>
                            <td className="py-2 px-2 text-center">{row.waste_count}</td>
                            <td className="py-2 px-2 text-center">{row.discrepancy_count}</td>
                          </>
                        )}
                        {reportType === 'inventory' && (
                          <>
                            <td className="py-2 px-2">{row.drug_name}</td>
                            <td className="py-2 px-2">{row.drug_strength}</td>
                            <td className="py-2 px-2 text-center">{row.dea_schedule}</td>
                            <td className="py-2 px-2">{row.lot_number || '—'}</td>
                            <td className="py-2 px-2">
                              {row.expiration_date
                                ? format(new Date(row.expiration_date), 'MM/dd/yyyy')
                                : '—'}
                            </td>
                            <td className="py-2 px-2 text-right">
                              {row.current_quantity} {row.unit_of_measure}
                            </td>
                            <td className="py-2 px-2">
                              {format(new Date(row.last_transaction_date), 'MM/dd/yyyy')}
                            </td>
                          </>
                        )}
                        {reportType === 'discrepancies' && (
                          <>
                            <td className="py-2 px-2">
                              {format(new Date(row.transaction_date), 'MM/dd/yyyy')}
                            </td>
                            <td className="py-2 px-2">{row.drug_name}</td>
                            <td className="py-2 px-2 text-center">{row.dea_schedule}</td>
                            <td className="py-2 px-2">{row.discrepancy_type}</td>
                            <td className="py-2 px-2 text-right">
                              {row.discrepancy_amount} {row.unit_of_measure}
                            </td>
                            <td className="py-2 px-2">{row.status}</td>
                            <td className="py-2 px-2 text-xs">{row.discrepancy_notes}</td>
                          </>
                        )}
                        {reportType === 'unverified_waste' && (
                          <>
                            <td className="py-2 px-2">
                              {format(new Date(row.transaction_date), 'MM/dd/yyyy')}
                            </td>
                            <td className="py-2 px-2">{row.drug_name}</td>
                            <td className="py-2 px-2 text-center">{row.dea_schedule}</td>
                            <td className="py-2 px-2 text-right">
                              {row.quantity_wasted} {row.unit_of_measure}
                            </td>
                            <td className="py-2 px-2">{row.provider_name}</td>
                            <td className="py-2 px-2">{row.patient_name || '—'}</td>
                            <td className="py-2 px-2 text-right">
                              {Math.round(row.hours_pending)}h
                            </td>
                          </>
                        )}
                        {reportType === 'provider_activity' && (
                          <>
                            <td className="py-2 px-2">{row.provider_name}</td>
                            <td className="py-2 px-2">{row.provider_dea_number || '—'}</td>
                            <td className="py-2 px-2">
                              {format(new Date(row.transaction_date), 'MM/dd/yyyy')}
                            </td>
                            <td className="py-2 px-2">{row.transaction_type}</td>
                            <td className="py-2 px-2">{row.drug_name}</td>
                            <td className="py-2 px-2 text-right">
                              {row.quantity} {row.unit_of_measure}
                            </td>
                            <td className="py-2 px-2">{row.patient_name || '—'}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Report Footer */}
            <div className="mt-8 pt-4 border-t-2 border-gray-800 text-sm text-gray-600">
              <div>Total Records: {reportData?.length || 0}</div>
              <div className="mt-4">
                <div className="font-bold mb-2">Certification:</div>
                <div className="mb-4">
                  I certify that the above information is accurate and complete to the best of my knowledge.
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="border-t border-gray-400 pt-2">Signature</div>
                  <div className="border-t border-gray-400 pt-2">Date</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
